import type { AxiosError } from 'axios';
import _ from 'lodash';
import type { Nullable } from '../types/common/utility';

export class ApiError extends Error {
  meta = {};

  developers = {};

  constructor(...args: any[]) {
    super(...args);
    const [message, options = {}] = args;
    const { meta, developers } = options;

    this.meta = meta;
    this.developers = developers;

    return this;
  }
}

export function isApiError(error: any) {
  return error instanceof ApiError;
}

/**
 * Safely read `error.response.data`. Network errors and cancelled requests
 * reach catch/retry handlers without a `response`, so a direct read throws.
 */
export const getResponseData = (error: any): unknown => error?.response?.data;

/**
 * Safely read `error.response.status`. Returns undefined when the error has no
 * response (e.g. network failure), avoiding a throw inside error handlers.
 */
export const getResponseStatus = (error: any): number | undefined =>
  error?.response?.status;

/**
 * Best-effort message from a thrown request error: the response body when
 * present, otherwise the error's own message. Safe when there is no response.
 */
export const getResponseDataOrMessage = (error: any): unknown =>
  getResponseData(error) ?? error?.message;

const GENERIC_SERVER_STATUS_CODES = Object.freeze([500, 502, 503, 504]);
export function manageAPIError(
  error: AxiosError | undefined,
  fallbackMessage: Nullable<string> = 'An error has occurred. Please try again later.'
) {
  if (
    error?.response &&
    GENERIC_SERVER_STATUS_CODES.includes(error.response.status)
  ) {
    return 'Our servers seem to be having trouble. Please try again later.';
  }

  const sanitizedErrorMessage = getSanitizedError(error);
  if (sanitizedErrorMessage) {
    return sanitizedErrorMessage;
  }

  const errorDetails = getErrorDetails(error);
  if (errorDetails) {
    return errorDetails;
  }

  const errorWithMessagePath = getErrorWithMessagePath(error);
  return errorWithMessagePath || fallbackMessage;
}

function getErrorWithMessagePath(error: AxiosError | undefined) {
  const errorMessagePath =
    typeof error?.response?.data === 'string' ? 'data' : 'data.message';

  return _.get(error?.response, errorMessagePath) as string | undefined;
}

const INVALID_PHONE_REGEX = /\[.*?: /;

/**
 * Coerce a response `detail` to a readable string. FastAPI returns a string
 * for HTTPException but an array of `{loc, msg, type}` objects for request
 * validation (422); without this, calling string methods on the array throws.
 */
function detailToString(detail: unknown): string | null {
  if (!detail) {
    return null;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) =>
        typeof d === 'string' ? d : d?.msg ?? d?.detail ?? JSON.stringify(d)
      )
      .filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  if (typeof detail === 'object') {
    const d = detail as { msg?: string; detail?: string };
    return d.msg ?? d.detail ?? JSON.stringify(detail);
  }
  return String(detail);
}

function sanitizeErrorDetail(
  detail: unknown,
  regexToRemove?: RegExp
): string | null {
  let cleanedDetail = detailToString(detail);
  if (!cleanedDetail) {
    return null;
  }
  if (regexToRemove) {
    cleanedDetail = cleanedDetail.replace(regexToRemove, '');
  }
  return cleanedDetail.replace(/\[/g, '').replace(/\]/g, '');
}

const getSanitizedError = (error: AxiosError | undefined) => {
  if (isErrorWithDetail(error)) {
    return sanitizeErrorDetail(
      error.response?.data.detail,
      INVALID_PHONE_REGEX
    );
  }

  return null;
};

function isErrorWithDetail(
  error: AxiosError<any> | undefined
): error is AxiosError<{ detail: string }> {
  return Boolean(error?.response?.data?.detail);
}

function getErrorDetails(error: AxiosError<any> | undefined) {
  if (!Array.isArray(error?.response?.data?.errors)) {
    return null;
  }

  return error?.response?.data?.errors.map((err: any) => err.detail).join(', ');
}
